import React, { forwardRef } from 'react';
import { OutlinedInput } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import type { FieldBaseProps } from './field.types';
import { FieldShell, describedBy } from './FieldShell';
import { useFieldIds } from './useFieldIds';
import { controlTextSx } from './fieldStyles';
import { useDateFnsLocale } from './datePickerLocale';
import { useDatePickerLocaleText } from './useDatePickerLocaleText';

export interface DateInputProps extends FieldBaseProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
  format?: string;
  readOnly?: boolean;
}

type ShellOnlyProps = Pick<FieldBaseProps, 'hideLabel' | 'describedById' | 'hideRequiredMark'>;

type PickerFieldProps = TextFieldProps & ShellOnlyProps;

const PickerField = forwardRef<HTMLDivElement, PickerFieldProps>(function PickerField(
  {
    id, label, hideLabel, helperText, describedById, error, required, hideRequiredMark, disabled, size, fullWidth = true, className, sx,
    value, onChange, onFocus, onBlur, onClick, placeholder, autoComplete, name, InputProps, inputProps, inputRef,
  },
  ref,
) {
  const ids = useFieldIds(id);
  const fieldSize = size === 'medium' ? 'medium' : 'small';
  return (
    <FieldShell ref={ref} ids={ids} label={label} hideLabel={hideLabel} helperText={helperText} error={error} required={required} hideRequiredMark={hideRequiredMark} disabled={disabled} size={fieldSize} fullWidth={fullWidth} className={className} sx={sx}>
      <OutlinedInput
        id={ids.id}
        ref={InputProps?.ref}
        inputRef={inputRef}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClick}
        placeholder={placeholder}
        readOnly={InputProps?.readOnly}
        autoComplete={autoComplete}
        name={name}
        inputProps={{ ...inputProps, 'aria-describedby': describedBy(ids, helperText, describedById) }}
        startAdornment={InputProps?.startAdornment}
        endAdornment={InputProps?.endAdornment}
        notched={false}
        sx={controlTextSx(fieldSize)}
      />
    </FieldShell>
  );
});

export const DateInput: React.FC<DateInputProps> = ({
  id, label, hideLabel, helperText, describedById, error, required, hideRequiredMark, disabled, size = 'small', fullWidth = true, className, sx,
  value, onChange, minDate, maxDate, disablePast, disableFuture, format, readOnly,
}) => {
  const adapterLocale = useDateFnsLocale();
  const localeText = useDatePickerLocaleText();
  const textField: PickerFieldProps = { id, label, hideLabel, helperText, describedById, error, required, hideRequiredMark, size, fullWidth, className, sx };
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
      <DatePicker
        value={value}
        onChange={(next) => onChange(next)}
        minDate={minDate}
        maxDate={maxDate}
        disablePast={disablePast}
        disableFuture={disableFuture}
        format={format}
        readOnly={readOnly}
        disabled={disabled}
        localeText={localeText}
        slots={{ textField: PickerField }}
        slotProps={{ textField }}
      />
    </LocalizationProvider>
  );
};
